import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Search, Menu, X, Building2, Bookmark, BarChart3, Zap } from 'lucide-react';
import { SavedFilterResponse, SearchFilterResponse } from '../../SearchFilter/Types/SerachFileter';
import { CandidateTotalCountResponse } from '../../Candidate/Types/CandidateType';
import ExecutiveSearchPortalLight, { ExecutiveSearchPortalRef } from '../SearchComponents/ExecutiveSearchPortalLight';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';

interface Props {
  searchFilterData: SearchFilterResponse[];
  candidatesTotalStats?: CandidateTotalCountResponse;
  savedSearchFilterDtaByUser?: SavedFilterResponse[];
  savedSearchFilterDtaByCompany?: SavedFilterResponse[];
}

const getQuickSearchItems = (stats?: CandidateTotalCountResponse) => [
  { value: "all", label: "All Candidates", count: stats?.allCandidates ?? 0 },
  { value: "active", label: "Actively Exploring", count: stats?.activelyExploringCandidates ?? 0 },
  { value: "new", label: "Newly Added", count: stats?.newlyCandidatesBy7days ?? 0 },
  { value: "recently_updated", label: "Recently Updated", count: stats?.recentlyUpdatedCandidatesBy7days ?? 0 },
  { value: "today_updated", label: "Today Updated Profiles", count: stats?.todayUpdatedCandidates ?? 0 },
  { value: "me", label: "Shortlisted By Me", count: stats?.invitedByMeCandidates ?? 0 },
  { value: "pending", label: "On Hold By Candidates", count: stats?.invitedPendingByMeCandidates ?? 0 },
  { value: "accepted", label: "Accepted By Candidates", count: stats?.invitedAcceptedByMeCandidates ?? 0 },
  { value: "rejected", label: "Rejected By Candidates", count: stats?.invitedRejectedByMeCandidates ?? 0 },
];

export const CandidateSearch = ({
  searchFilterData,
  candidatesTotalStats,
  savedSearchFilterDtaByUser,
  savedSearchFilterDtaByCompany,
}: Props) => {
  const [isPresetsOpen, setIsPresetsOpen] = useState(false);
  const [selectedQuickSearch, setSelectedQuickSearch] = useState<string>("all");
  const searchPortalRef = useRef<ExecutiveSearchPortalRef>(null);
  const navigate = useRedirect();

  const quickSearchItems = getQuickSearchItems(candidatesTotalStats);

  const handleQuickSearchClick = (status: string) => {
    setSelectedQuickSearch(status);
    setIsPresetsOpen(false);
    navigate(`/recruiter/candidates/listing/${status}`);
  };

  const handleApplySavedFilter = (filter: SavedFilterResponse) => {
    searchPortalRef.current?.applySavedFilter(filter.filterJson, filter);
    setIsPresetsOpen(false);
  };

  const statsStrip = [
    { label: "Total", value: candidatesTotalStats?.allCandidates ?? 0 },
    { label: "Actively Exploring", value: candidatesTotalStats?.activelyExploringCandidates ?? 0 },
    { label: "Accepted", value: candidatesTotalStats?.invitedAcceptedByMeCandidates ?? 0 },
  ];

  const renderSavedFilters = (
    title: string,
    filters: SavedFilterResponse[] | undefined,
    showCompanyTag = false
  ) => (
    <div className="p-4 border-t border-slate-800/60">
      <p className="text-[10px] font-mono font-bold tracking-widest text-slate-500 uppercase mb-3">
        {title}
      </p>
      {!filters || filters.length === 0 ? (
        <p className="text-xs text-slate-600 px-3 py-1.5">No saved searches yet.</p>
      ) : (
        <nav className="space-y-0.5 text-sm text-slate-400">
          {filters.slice(0, 20).map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleApplySavedFilter(filter)}
              className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-slate-800/50 hover:text-slate-200 rounded-lg transition-colors text-left group"
            >
              <span className="truncate flex items-center gap-2">
                <Bookmark className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-400 shrink-0" />
                <span className="truncate">{filter.name}</span>
              </span>
              {filter.isDefault && (
                <span className="text-amber-500 text-xs drop-shadow-[0_0_4px_rgba(245,158,11,0.3)] shrink-0">★</span>
              )}
            </button>
          ))}
        </nav>
      )}
    </div>
  );

  const renderQuickSearchNav = () => (
    <div className="p-4">
      <p className="text-[10px] font-mono font-bold tracking-widest text-cyan-400/80 uppercase mb-3">
        // Quick Search
      </p>
      <nav className="space-y-1">
        {quickSearchItems.map((item) => {
          const isActive = selectedQuickSearch === item.value;
          return (
            <button
              key={item.value}
              onClick={() => handleQuickSearchClick(item.value)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-xl transition-all duration-150 ${
                isActive
                  ? "bg-cyan-500/10 text-cyan-400 font-bold border border-cyan-500/20 shadow-inner"
                  : "text-slate-400 font-medium hover:bg-slate-800/60 hover:text-slate-200"
              }`}
            >
              <span>{item.label}</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-md font-mono ${
                  isActive ? "bg-cyan-900/40 text-cyan-400 font-bold" : "bg-slate-950 text-slate-500 font-semibold"
                }`}
              >
                {item.count.toLocaleString()}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );

  const renderCompanyFilters = () => {
    if (!savedSearchFilterDtaByCompany || savedSearchFilterDtaByCompany.length === 0) return null;
    return (
      <div className="p-4 border-t border-slate-800/60">
        <p className="text-[10px] font-mono font-bold tracking-widest text-slate-500 uppercase mb-3">
          Company Saved Searches
        </p>
        <nav className="space-y-0.5 text-sm text-slate-400">
          {savedSearchFilterDtaByCompany.slice(0, 10).map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleApplySavedFilter(filter)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-800/50 hover:text-slate-200 rounded-lg transition-colors group text-left"
            >
              <Building2 className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-400 shrink-0" />
              <span className="truncate text-xs font-semibold text-slate-300 group-hover:text-white">
                {filter.name}
              </span>
            </button>
          ))}
        </nav>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen w-full bg-slate-950 text-slate-100 font-sans antialiased overflow-x-hidden selection:bg-cyan-500/30 selection:text-cyan-200">
      {/* ================= DESKTOP SIDEBAR ================= */}
      <aside className="hidden lg:flex w-64 border-r border-slate-800/80 bg-slate-900 flex-col overflow-y-auto shrink-0 select-none sticky top-0 h-screen">
        <div>
          {/* Logo Section */}
          <div className="p-5 flex items-center gap-2 border-b border-slate-800/60">
            <div className="h-8 w-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-slate-950 font-black text-xl shadow-md shadow-cyan-500/10">
              N
            </div>
            <span className="text-xl font-black tracking-wider bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
              Nexgent
            </span>
          </div>

          {renderQuickSearchNav()}
          {renderSavedFilters("Saved Searches (Me)", savedSearchFilterDtaByUser)}
        </div>
        {renderCompanyFilters()}
      </aside>

      {/* ================= MAIN INTERFACE ================= */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Top Bar */}
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-slate-800/80 bg-slate-900/95 backdrop-blur-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-slate-950 font-black text-lg shadow-md shadow-cyan-500/10">
              N
            </div>
            <div>
              <p className="text-sm font-black tracking-tight text-slate-100">Talent Search</p>
              <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-cyan-400">
                {candidatesTotalStats?.allCandidates ?? 0} candidates
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsPresetsOpen(true)}
            className="inline-flex items-center gap-2 text-xs font-bold text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 px-3 py-2 rounded-xl transition-all"
          >
            <Menu className="w-4 h-4" />
            <span className="hidden sm:inline">Search Presets</span>
          </button>
        </header>

        {/* Mobile Stats Strip */}
        {candidatesTotalStats && (
          <div className="lg:hidden overflow-x-auto no-scrollbar border-b border-slate-800/60 bg-slate-900/50 px-3 py-2">
            <div className="flex gap-2 min-w-max">
              {statsStrip.map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-2 rounded-xl bg-slate-800/70 border border-slate-700/50 px-3 py-1.5"
                >
                  <BarChart3 className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-xs font-bold text-slate-300">{stat.label}</span>
                  <span className="text-xs font-mono font-bold text-cyan-400">{stat.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-3 sm:p-5 lg:p-6 bg-slate-950">
          <ExecutiveSearchPortalLight ref={searchPortalRef} />
        </main>
      </div>

      {/* ================= MOBILE PRESETS DRAWER ================= */}
      <Transition show={isPresetsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={() => setIsPresetsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="fixed inset-y-0 left-0 w-full max-w-xs bg-slate-900 border-r border-slate-800 shadow-2xl overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-slate-800/60">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-black tracking-tight">Search Presets</p>
                </div>
                <button
                  onClick={() => setIsPresetsOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-3">
                  <Search className="w-3.5 h-3.5" />
                  <p className="text-[10px] font-mono font-bold tracking-widest uppercase">
                    Drill into segments
                  </p>
                </div>
              </div>

              {renderQuickSearchNav()}
              {renderSavedFilters("Saved Searches (Me)", savedSearchFilterDtaByUser)}
              {renderCompanyFilters()}
            </div>
          </Transition.Child>
        </Dialog>
      </Transition>
    </div>
  );
};